import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { CreateManualSong as BLCreateManualSong } from '../../../../utilities/BL/Admin/Overlays/Playlist';

/**
 * MODAL DE ALTA MANUAL DE CANCIÓN O ARTISTA [HU-07]
 * 
 * Permite al DJ, Operador de Cabina o Administrador ingresar una pista o artista directamente,
 * con opción de agregarla de inmediato a la Playlist Oficial Activa (Favorita ⭐).
 * 
 * @component
 * @param {Object} props
 * @param {boolean} [props.isOpen=true] - Visibilidad del modal.
 * @param {Function} props.onClose - Callback al cerrar el modal.
 * @param {Function} [props.onSuccess] - Callback al guardar exitosamente la canción.
 * @param {string|null} [props.roomId=null] - Identificador GUID de la sala activa.
 * @param {string} [props.defaultOperatorName="Operador de Sonido"] - Nombre por defecto del operador.
 */
export const PlaylistManualSongModal = ({
    isOpen = true,
    onClose = () => {},
    onSuccess = () => {},
    roomId = null,
    defaultOperatorName = 'Operador de Sonido'
}) => {
    const { showMessage } = useFloatMessage();

    // Pestaña activa: 1 = "Sugerir Canción 🎵", 2 = "Sugerir Artista 🎤"
    const [activeTab, setActiveTab] = useState(1);

    // Estado del formulario
    const [formData, setFormData] = useState({
        title: '',
        artistName: '',
        operatorName: defaultOperatorName || 'Operador de Sonido',
        isFavorite: true
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Manejador de teclado para cerrar con ESC
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && !isSubmitting) {
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, isSubmitting, onClose]);

    if (!isOpen) return null;

    /**
     * Envía el formulario para persistir la canción manual a través de BL.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanTitle = (formData.title || '').trim();
        const cleanArtist = (formData.artistName || '').trim();
        const cleanOperator = (formData.operatorName || '').trim() || defaultOperatorName;

        if (!cleanTitle) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: activeTab === 1
                    ? 'Por favor ingresa el título de la canción.'
                    : 'Por favor ingresa el nombre del artista.'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                roomId: roomId || null,
                suggestionType: activeTab, // 1: Canción, 2: Artista
                title: cleanTitle,
                artistName: cleanArtist,
                operatorName: cleanOperator,
                isFavorite: formData.isFavorite,
                isManualEntry: true
            };

            const response = await BLCreateManualSong(payload);

            if (response.success && response.data) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `🎵 Pista "${response.data.title || cleanTitle}" registrada exitosamente.`
                });
                onSuccess(response.data);
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'No fue posible agregar la pista manual.'
                });
            }
        } catch (error) {
            console.error('[PlaylistManualSongModal] Error al crear canción manual:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado de red al registrar la pista.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="PlaylistModal">
            {/* Backdrop con clic para cerrar */}
            <div
                className="PlaylistModal__Backdrop"
                onClick={() => !isSubmitting && onClose()}
                aria-hidden="true"
            />

            {/* Contenedor del Modal */}
            <div className="PlaylistModal__Container" role="dialog" aria-modal="true">
                {/* Encabezado */}
                <div className="PlaylistModal__Header">
                    <div className="PlaylistModal__HeaderInfo">
                        <span className="PlaylistModal__HeaderIcon">🎧</span>
                        <div>
                            <h2 className="PlaylistModal__Title">Alta Rápida de Canción / Artista</h2>
                            <span className="PlaylistModal__Subtitle">
                                Añade canciones oficiales o peticiones especiales a la consola
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="PlaylistModal__CloseBtn"
                        onClick={onClose}
                        disabled={isSubmitting}
                        title="Cerrar modal (ESC)"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                {/* Pestañas Luminosas (Tabs) */}
                <div className="PlaylistModal__Tabs">
                    <button
                        type="button"
                        className={`PlaylistModal__Tab ${activeTab === 1 ? 'PlaylistModal__Tab--Active' : ''}`}
                        onClick={() => setActiveTab(1)}
                        disabled={isSubmitting}
                    >
                        <span>🎵</span> Sugerir Canción
                    </button>
                    <button
                        type="button"
                        className={`PlaylistModal__Tab ${activeTab === 2 ? 'PlaylistModal__Tab--Active' : ''}`}
                        onClick={() => setActiveTab(2)}
                        disabled={isSubmitting}
                    >
                        <span>🎤</span> Sugerir Artista
                    </button>
                </div>

                {/* Formulario */}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    <div className="PlaylistModal__Body">
                        {/* TAB 1: MODO CANCIÓN */}
                        {activeTab === 1 && (
                            <>
                                <div className="PlaylistModal__FormGroup">
                                    <label className="PlaylistModal__Label">
                                        Título de la Canción <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="PlaylistModal__Input"
                                        placeholder="Ej. La Chona, Cielito Lindo, Don't Stop Believin'..."
                                        value={formData.title}
                                        maxLength={200}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        disabled={isSubmitting}
                                        autoFocus
                                        required
                                    />
                                </div>

                                <div className="PlaylistModal__FormGroup">
                                    <label className="PlaylistModal__Label">
                                        Intérprete / Grupo Musical <span className="PlaylistModal__Hint">(Opcional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="PlaylistModal__Input"
                                        placeholder="Ej. Los Tucanes de Tijuana, Journey, Queen..."
                                        value={formData.artistName}
                                        maxLength={200}
                                        onChange={(e) => setFormData({ ...formData, artistName: e.target.value })}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </>
                        )}

                        {/* TAB 2: MODO ARTISTA */}
                        {activeTab === 2 && (
                            <>
                                <div className="PlaylistModal__FormGroup">
                                    <label className="PlaylistModal__Label">
                                        Nombre del Artista / Grupo <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="PlaylistModal__Input"
                                        placeholder="Ej. Bad Bunny, Dua Lipa, Luis Miguel, Coldplay..."
                                        value={formData.title}
                                        maxLength={200}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        disabled={isSubmitting}
                                        autoFocus
                                        required
                                    />
                                </div>

                                <div className="PlaylistModal__FormGroup">
                                    <label className="PlaylistModal__Label">
                                        Tema Recomendado / Referencia <span className="PlaylistModal__Hint">(Opcional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="PlaylistModal__Input"
                                        placeholder="Ej. Grandes Éxitos, Canción representativa..."
                                        value={formData.artistName}
                                        maxLength={200}
                                        onChange={(e) => setFormData({ ...formData, artistName: e.target.value })}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </>
                        )}

                        {/* Nombre del Operador / Creador */}
                        <div className="PlaylistModal__FormGroup">
                            <label className="PlaylistModal__Label">
                                Registrado por / Creador
                            </label>
                            <input
                                type="text"
                                className="PlaylistModal__Input"
                                placeholder="Ej. Operador de Sonido, DJ Principal..."
                                value={formData.operatorName}
                                maxLength={100}
                                onChange={(e) => setFormData({ ...formData, operatorName: e.target.value })}
                                disabled={isSubmitting}
                            />
                        </div>

                        {/* Switch: Agregar directo a Playlist Activa (Favorita) */}
                        <div
                            className={`PlaylistModal__SwitchControl ${formData.isFavorite ? 'PlaylistModal__SwitchControl--Active' : ''}`}
                            onClick={() => !isSubmitting && setFormData({ ...formData, isFavorite: !formData.isFavorite })}
                        >
                            <div className="PlaylistModal__SwitchLabel">
                                <span className="PlaylistModal__SwitchTitle">
                                    ⭐ ¿Agregar directo a la Playlist Activa (Favorita)?
                                </span>
                                <span className="PlaylistModal__SwitchDesc">
                                    Si se activa, entrará inmediatamente en la cola de reproducción del estadio y cabina.
                                </span>
                            </div>
                            <input
                                type="checkbox"
                                className="form-check-input ms-3"
                                checked={formData.isFavorite}
                                onChange={(e) => setFormData({ ...formData, isFavorite: e.target.checked })}
                                disabled={isSubmitting}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>

                    {/* Pie del Modal */}
                    <div className="PlaylistModal__Footer">
                        <button
                            type="button"
                            className="PlaylistModal__Btn PlaylistModal__Btn--Secondary"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="PlaylistModal__Btn PlaylistModal__Btn--Primary"
                            disabled={isSubmitting || !formData.title.trim()}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="PlaylistModal__Spinner"></span>
                                    <span>Guardando...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-plus-circle-fill"></i>
                                    <span>Guardar Pista</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

PlaylistManualSongModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    roomId: PropTypes.string,
    defaultOperatorName: PropTypes.string
};

export default PlaylistManualSongModal;
